import { Box, Typography, Button } from "@mui/material";
import { FiUploadCloud } from "react-icons/fi";
import { useState } from "react";
import { MdOutlineFileDownload } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";

const FileUploadBox = ({ label, file, setFile, inputId }) => {
  const [error, setError] = useState("");

  const MAX_SIZE = 50 * 1024 * 1024;

  const validateFile = (selected) => {
    if (!selected) return false;

    if (selected.type !== "application/pdf") {
      setError("Only PDF files are allowed");
      return false;
    }

    if (selected.size > MAX_SIZE) {
      setError("File size must be less than 50MB");
      return false;
    }

    setError("");
    return true;
  };

  const handleChange = (e) => {
    const selected = e.target.files[0];
    if (validateFile(selected)) {
      setFile(selected);
    }
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];

    if (validateFile(droppedFile)) {
      setFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setFile(null);
    setError("");
  };

  const truncateName = (name) => {
    if (!name) return "";
    if (name.length <= 25) return name;
    return name.slice(0, 15) + "..." + name.slice(-7);
  };

  const isUrl = typeof file === "string";

  const getFileNameFromUrl = (url) => {
    try {
      return url.split("/").pop().split("?")[0];
    } catch {
      return "Uploaded File";
    }
  };

  const fileName = isUrl ? getFileNameFromUrl(file) : file?.name;

  return (
    <div>
      <label className="form-label">{label}</label>

      <Box
        onClick={() => document.getElementById(inputId).click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          border: "2px dashed #cbd5e1",
          borderRadius: "12px",
          p: 3,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: "#f8fafc",
          transition: "0.3s",
          "&:hover": {
            borderColor: "#0d6efd",
            backgroundColor: "#f1f5f9",
          },
        }}
      >
        {!file && (
          <>
            <FiUploadCloud style={{ fontSize: 40, color: "#64748b" }} />

            <Typography variant="body1" mt={1}>
              Choose a file or drag & drop it here
            </Typography>

            <Typography variant="caption" color="text.secondary">
              PDF format, up to 50MB
            </Typography>

            <Box mt={2}>
              <Button variant="outlined" size="small">
                Browse File
              </Button>
            </Box>
          </>
        )}

        {file && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "#fff",
              borderRadius: "8px",
              p: 1,
              border: "1px solid #e2e8f0",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <span className="badge bg-danger">PDF</span>

              <Typography variant="body2" title={fileName}>
                {truncateName(fileName)}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
              {(isUrl) && (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(file, "_blank");
                  }}
                  sx={{
                    minWidth: "28px",
                    width: "28px",
                    height: "28px",
                    p: 0,
                    borderRadius: "6px",
                  }}
                >
                  <MdOutlineFileDownload size={18} />
                </Button>
              )}

              <Button
                onClick={handleRemove}
                sx={{
                  minWidth: "28px",
                  width: "28px",
                  height: "28px",
                  p: 0,
                  borderRadius: "6px",
                  color: "error.main",
                }}
              >
                <RxCross2 size={18} />
              </Button>
            </Box>
          </Box>
        )}

        {/* Hidden Input */}
        <input
          id={inputId}
          type="file"
          accept="application/pdf"
          hidden
          onChange={handleChange}
        />
      </Box>

      {error && (
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      )}
    </div>
  );
};

export default FileUploadBox;
